"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertCircle, AlertTriangle, Check, ChevronDown, ChevronLeft, ChevronRight, Code, Copy, Eye, Gift, Globe, MessageSquare, Wrench, Zap, Search, Filter, X, ArrowUpDown, ArrowUp, ArrowDown, Video, ImagePlus, ExternalLink, Percent, Scale, Braces, FileJson2, List, Grid, Bot, Boxes, Brain, Sparkles, PenTool, Sliders, Volume2 } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useMemo, useState, useCallback, useEffect } from "react";
import Footer from "@/components/landing/footer";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card, CardContent } from "@/lib/components/card";
import { Input } from "@/lib/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/components/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/components/table";
import { Toggle } from "@/lib/components/toggle";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/lib/components/tooltip";
import { cn, formatDeprecationDate } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import { getProviderIcon } from "@llmgateway/shared/components";
import { ModelCard } from "./model-card";
import type { ApiModel, ApiModelProviderMapping, ApiProvider } from "@/lib/fetch-models";
import type { StabilityLevel } from "@llmgateway/models";
interface ModelWithProviders extends ApiModel {
  providerDetails: Array<{
    provider: ApiModelProviderMapping;
    providerInfo: ApiProvider;
  }>;
}
interface AllModelsProps {
  children: React.ReactNode;
  models: ApiModel[];
  providers: ApiProvider[];
  title?: string;
  description?: string;
  categoryFilter?: "text" | "text-to-image" | "image-to-image" | "video" | "embedding" | "web-search" | "vision" | "reasoning" | "tools" | "discounted";
}
type SortField = "provider" | "name" | "inputPrice" | "outputPrice" | "cachedInputPrice";
type SortDirection = "asc" | "desc";
type Translate = (key: string, values?: Record<string, string | number | boolean>) => string;

// Capability icon type
interface CapabilityIcon {
  icon: React.ComponentType<{
    className?: string;
  }>;
  label: string;
  color: string;
}

// Flattened row structure for table view
interface FlattenedModelRow {
  model: ApiModel;
  provider: ApiModelProviderMapping;
  providerInfo: ApiProvider;
  hasAdditionalPricing: boolean;
  rowKey: string;
  capabilities: CapabilityIcon[];
  ProviderIcon: React.ComponentType<{
    className?: string;
  }> | null;
}

// Helper to compute capabilities (moved outside component for performance)
function computeCapabilities(provider: ApiModelProviderMapping, model: ApiModel, t: Translate): CapabilityIcon[] {
  const capabilities: CapabilityIcon[] = [];
  if (provider.streaming) {
    capabilities.push({
      icon: Zap,
      label: t("public.capabilities.streaming"),
      color: "text-blue-500"
    });
  }
  if (provider.vision) {
    capabilities.push({
      icon: Eye,
      label: t("public.capabilities.vision"),
      color: "text-green-500"
    });
  }
  if (provider.tools) {
    capabilities.push({
      icon: Wrench,
      label: t("public.capabilities.tools"),
      color: "text-purple-500"
    });
  }
  if (provider.reasoning) {
    capabilities.push({
      icon: MessageSquare,
      label: t("public.capabilities.reasoning"),
      color: "text-orange-500"
    });
  }
  if (provider.reasoningMaxTokens) {
    capabilities.push({
      icon: Sliders,
      label: t("public.capabilities.reasoningBudget"),
      color: "text-amber-500"
    });
  }
  if (provider.jsonOutput) {
    capabilities.push({
      icon: Braces,
      label: t("public.capabilities.jsonOutput"),
      color: "text-cyan-500"
    });
  }
  if (provider.jsonOutputSchema) {
    capabilities.push({
      icon: FileJson2,
      label: t("public.capabilities.structuredJson"),
      color: "text-teal-500"
    });
  }
  if (model?.output?.includes("image")) {
    capabilities.push({
      icon: ImagePlus,
      label: t("public.capabilities.imageGeneration"),
      color: "text-pink-500"
    });
  }
  if (model?.output?.includes("video")) {
    capabilities.push({
      icon: Video,
      label: t("public.capabilities.videoGeneration"),
      color: "text-violet-500"
    });
  }
  if (model?.output?.includes("audio")) {
    capabilities.push({
      icon: Volume2,
      label: t("public.capabilities.audioGeneration"),
      color: "text-rose-500"
    });
  }
  if (model?.output?.includes("embedding")) {
    capabilities.push({
      icon: Boxes,
      label: t("public.capabilities.embedding"),
      color: "text-indigo-500"
    });
  }
  if (provider.webSearch) {
    capabilities.push({
      icon: Globe,
      label: t("public.capabilities.webSearch"),
      color: "text-sky-500"
    });
  }
  return capabilities;
}

// Memoized table row component for performance
const ModelTableRow = React.memo(t0 => {
  const $ = _c(97);
  const {
    row,
    isExpanded,
    copiedModel,
    onToggleExpand,
    onCopy,
    onNavigate,
    formatPrice,
    t
  } = t0;
  const {
    ProviderIcon
  } = row;
  let t1;
  if ($[0] !== isExpanded || $[1] !== onToggleExpand || $[2] !== row.hasAdditionalPricing) {
    t1 = row.hasAdditionalPricing ? <button onClick={e => {
      e.stopPropagation();
      onToggleExpand();
    }} className="p-0.5 hover:bg-muted rounded">{isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}</button> : <div className="w-5 h-5" />;
    $[0] = isExpanded;
    $[1] = onToggleExpand;
    $[2] = row.hasAdditionalPricing;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== ProviderIcon || $[5] !== row.provider.providerId || $[6] !== row.providerInfo?.color || $[7] !== row.providerInfo?.name) {
    t2 = ProviderIcon ? <ProviderIcon className="w-4 h-4" /> : <div className="w-4 h-4 rounded-sm flex items-center justify-center text-xs font-medium text-white" style={{
      backgroundColor: row.providerInfo?.color ?? "#6b7280"
    }}>{(row.providerInfo?.name ?? row.provider.providerId).charAt(0).toUpperCase()}</div>;
    $[4] = ProviderIcon;
    $[5] = row.provider.providerId;
    $[6] = row.providerInfo?.color;
    $[7] = row.providerInfo?.name;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = row.providerInfo?.name ?? row.provider.providerId;
  let t4;
  if ($[9] !== row.provider.region) {
    t4 = row.provider.region && <span className="text-muted-foreground text-xs ml-1">({row.provider.region})</span>;
    $[9] = row.provider.region;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <span className="text-sm">{t3}{t4}</span>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== row.provider.deactivatedAt) {
    t6 = row.provider.deactivatedAt && <Tooltip><TooltipTrigger asChild={true}><span className="shrink-0 cursor-help"><AlertCircle className="h-3.5 w-3.5 text-red-500" /></span></TooltipTrigger><TooltipContent><p className="text-xs">{formatDeprecationDate(row.provider.deactivatedAt, "deactivated")}</p></TooltipContent></Tooltip>;
    $[14] = row.provider.deactivatedAt;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== row.provider.deactivatedAt || $[17] !== row.provider.deprecatedAt) {
    t7 = !row.provider.deactivatedAt && row.provider.deprecatedAt && <Tooltip><TooltipTrigger asChild={true}><span className="shrink-0 cursor-help"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span></TooltipTrigger><TooltipContent><p className="text-xs">{formatDeprecationDate(row.provider.deprecatedAt, "deprecated")}</p></TooltipContent></Tooltip>;
    $[16] = row.provider.deactivatedAt;
    $[17] = row.provider.deprecatedAt;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ExternalLink className="h-3 w-3 text-muted-foreground" />;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t9 = <TableCell className="font-medium"><div className="flex items-center gap-2">{t1}{t2}{t5}{t6}{t7}{t8}</div></TableCell>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = `/models/${encodeURIComponent(row.model.id)}`;
  let t11;
  if ($[26] !== row.model.id || $[27] !== t10) {
    t11 = <Link href={t10} onClick={_temp} className="font-medium text-sm hover:text-primary hover:underline">{row.model.id}</Link>;
    $[26] = row.model.id;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== onCopy || $[30] !== row.model.id || $[31] !== row.provider.providerId || $[32] !== row.provider.region || $[33] !== row.rowKey) {
    t12 = e_1 => {
      const fullId = row.provider.region ? `${row.provider.providerId}/${row.model.id}:${row.provider.region}` : `${row.provider.providerId}/${row.model.id}`;
      onCopy(fullId, row.rowKey, e_1);
    };
    $[29] = onCopy;
    $[30] = row.model.id;
    $[31] = row.provider.providerId;
    $[32] = row.provider.region;
    $[33] = row.rowKey;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== copiedModel || $[36] !== row.rowKey || $[37] !== t) {
    t13 = copiedModel === row.rowKey ? t("public.common.copied") : t("public.common.copyModelId");
    $[35] = copiedModel;
    $[36] = row.rowKey;
    $[37] = t;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== copiedModel || $[40] !== row.rowKey) {
    t14 = copiedModel === row.rowKey ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3 text-muted-foreground" />;
    $[39] = copiedModel;
    $[40] = row.rowKey;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t12 || $[43] !== t13 || $[44] !== t14) {
    t15 = <button onClick={t12} className="p-1 hover:bg-muted rounded transition-colors" title={t13}>{t14}</button>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t11 || $[47] !== t15) {
    t16 = <TableCell><div className="flex items-center gap-2">{t11}{t15}</div></TableCell>;
    $[46] = t11;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== formatPrice || $[50] !== row.provider.discount || $[51] !== row.provider.inputCharacterPrice || $[52] !== row.provider.inputPrice || $[53] !== row.provider.perSecondPrice || $[54] !== row.provider.requestPrice || $[55] !== t) {
    t17 = row.provider.perSecondPrice && !row.provider.inputPrice ? <Tooltip><TooltipTrigger asChild={true}><span className="text-violet-500 cursor-help">{(() => {
            const prices = row.provider.perSecondPrice;
            const values = Object.values(prices).map(Number).filter(Number.isFinite);
            if (values.length === 0) {
              return "\u2014";
            }
            const min = Math.min(...values);
            return `$${min}/sec`;
          })()}</span></TooltipTrigger><TooltipContent><p className="text-xs">{t("public.models.list.videoSecondPricing")}</p></TooltipContent></Tooltip> : (!row.provider.inputPrice || parseFloat(row.provider.inputPrice) === 0) && row.provider.inputCharacterPrice && parseFloat(row.provider.inputCharacterPrice) > 0 ? <Tooltip><TooltipTrigger asChild={true}><span className="text-sky-500 cursor-help">${parseFloat((parseFloat(row.provider.inputCharacterPrice) * 1000).toFixed(4))}/1K chars</span></TooltipTrigger><TooltipContent><p className="text-xs">{t("public.models.list.perCharacterPricing")}</p></TooltipContent></Tooltip> : (!row.provider.inputPrice || parseFloat(row.provider.inputPrice) === 0) && row.provider.requestPrice && parseFloat(row.provider.requestPrice) > 0 ? <Tooltip><TooltipTrigger asChild={true}><span className="text-amber-500 cursor-help">${parseFloat(row.provider.requestPrice).toFixed(3)}/req</span></TooltipTrigger><TooltipContent><p className="text-xs">{t("public.models.list.perRequestPricing")}</p></TooltipContent></Tooltip> : formatPrice(row.provider.inputPrice, row.provider.discount);
    $[49] = formatPrice;
    $[50] = row.provider.discount;
    $[51] = row.provider.inputCharacterPrice;
    $[52] = row.provider.inputPrice;
    $[53] = row.provider.perSecondPrice;
    $[54] = row.provider.requestPrice;
    $[55] = t;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t17) {
    t18 = <TableCell className="text-right font-mono text-sm">{t17}</TableCell>;
    $[57] = t17;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== formatPrice || $[60] !== row.provider.discount || $[61] !== row.provider.inputCharacterPrice || $[62] !== row.provider.outputPrice || $[63] !== row.provider.perSecondPrice || $[64] !== row.provider.requestPrice) {
    t19 = row.provider.perSecondPrice && !row.provider.outputPrice ? <Tooltip><TooltipTrigger asChild={true}><span className="text-violet-500 cursor-help">{(() => {
            const prices_0 = row.provider.perSecondPrice;
            const values_0 = Object.values(prices_0).map(Number).filter(Number.isFinite);
            if (values_0.length === 0) {
              return "\u2014";
            }
            const max = Math.max(...values_0);
            return `$${max}/sec`;
          })()}</span></TooltipTrigger><TooltipContent><p className="text-xs">Video per-second pricing</p></TooltipContent></Tooltip> : (!row.provider.outputPrice || parseFloat(row.provider.outputPrice) === 0) && (row.provider.requestPrice && parseFloat(row.provider.requestPrice) > 0 || row.provider.inputCharacterPrice && parseFloat(row.provider.inputCharacterPrice) > 0) ? <span className="text-muted-foreground">—</span> : formatPrice(row.provider.outputPrice, row.provider.discount);
    $[59] = formatPrice;
    $[60] = row.provider.discount;
    $[61] = row.provider.inputCharacterPrice;
    $[62] = row.provider.outputPrice;
    $[63] = row.provider.perSecondPrice;
    $[64] = row.provider.requestPrice;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  let t20;
  if ($[66] !== t19) {
    t20 = <TableCell className="text-right font-mono text-sm">{t19}</TableCell>;
    $[66] = t19;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== formatPrice || $[69] !== row.provider.cachedInputPrice || $[70] !== row.provider.discount) {
    t21 = formatPrice(row.provider.cachedInputPrice, row.provider.discount);
    $[68] = formatPrice;
    $[69] = row.provider.cachedInputPrice;
    $[70] = row.provider.discount;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t21) {
    t22 = <TableCell className="text-right font-mono text-sm">{t21}</TableCell>;
    $[72] = t21;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== row.capabilities) {
    t23 = row.capabilities.slice(0, 4).map(_temp2);
    $[74] = row.capabilities;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] !== t23) {
    t24 = <TableCell className="text-center"><div className="flex justify-center gap-1">{t23}</div></TableCell>;
    $[76] = t23;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] !== onNavigate || $[79] !== t16 || $[80] !== t18 || $[81] !== t20 || $[82] !== t22 || $[83] !== t24 || $[84] !== t9) {
    t25 = <TableRow className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={onNavigate}>{t9}{t16}{t18}{t20}{t22}{t24}</TableRow>;
    $[78] = onNavigate;
    $[79] = t16;
    $[80] = t18;
    $[81] = t20;
    $[82] = t22;
    $[83] = t24;
    $[84] = t9;
    $[85] = t25;
  } else {
    t25 = $[85];
  }
  let t26;
  if ($[86] !== isExpanded || $[87] !== row.hasAdditionalPricing || $[88] !== row.provider.perSecondPrice || $[89] !== row.provider.requestPrice || $[90] !== row.provider.webSearch || $[91] !== row.provider.webSearchPrice || $[92] !== t) {
    t26 = isExpanded && row.hasAdditionalPricing && <TableRow className="bg-muted/30"><TableCell colSpan={6} className="py-3"><div className="pl-8"><div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t("public.models.list.additionalPricing")}</div><div className="flex gap-3">{row.provider.webSearch && <Badge variant="outline" className="text-sm px-3 py-1.5 bg-background"><Globe className="h-4 w-4 mr-2 text-purple-500" />{t("public.capabilities.webSearch")}{row.provider.webSearchPrice && parseFloat(row.provider.webSearchPrice) > 0 && ` $${parseFloat(row.provider.webSearchPrice).toFixed(3)}/search`}</Badge>}{row.provider.requestPrice && parseFloat(row.provider.requestPrice) > 0 && <Badge variant="outline" className="text-sm px-3 py-1.5 bg-background">{t("public.models.list.perRequest")} ${parseFloat(row.provider.requestPrice).toFixed(3)}</Badge>}{row.provider.perSecondPrice && Object.keys(row.provider.perSecondPrice).length > 0 && <Badge variant="outline" className="text-sm px-3 py-1.5 bg-background"><Video className="h-4 w-4 mr-2 text-violet-500" />Video{" "}{(() => {
                const prices_1 = row.provider.perSecondPrice;
                const defaultVideo = prices_1.default_video;
                const defaultAudio = prices_1.default_audio;
                const defaultPrice = prices_1["default"];
                if (defaultVideo && defaultAudio) {
                  return `$${defaultVideo} – $${defaultAudio}/sec`;
                }
                if (defaultPrice) {
                  return `$${defaultPrice}/sec`;
                }
                const firstValue = Object.values(prices_1)[0];
                return firstValue ? `$${firstValue}/sec` : "";
              })()}</Badge>}</div></div></TableCell></TableRow>;
    $[86] = isExpanded;
    $[87] = row.hasAdditionalPricing;
    $[88] = row.provider.perSecondPrice;
    $[89] = row.provider.requestPrice;
    $[90] = row.provider.webSearch;
    $[91] = row.provider.webSearchPrice;
    $[92] = t;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  let t27;
  if ($[94] !== t25 || $[95] !== t26) {
    t27 = <>{t25}{t26}</>;
    $[94] = t25;
    $[95] = t26;
    $[96] = t27;
  } else {
    t27 = $[96];
  }
  return t27;
});
const MODELS_PER_PAGE = 50;
function applyCategoryFilter(categoryFilter: AllModelsProps["categoryFilter"], model: ApiModel, providerDetails: ModelWithProviders["providerDetails"]): boolean {
  switch (categoryFilter) {
    case "text":
      return !model.output?.includes("image") && !model.output?.includes("video") && !model.output?.includes("embedding");
    case "text-to-image":
      return model.output?.includes("image") === true;
    case "image-to-image":
      return model.output?.includes("image") === true && providerDetails.some(p => p.provider.vision);
    case "video":
      return model.output?.includes("video") === true;
    case "embedding":
      return model.output?.includes("embedding") === true;
    case "web-search":
      return providerDetails.some(p => p.provider.webSearch);
    case "vision":
      return providerDetails.some(p => p.provider.vision);
    case "reasoning":
      return providerDetails.some(p => p.provider.reasoning);
    case "tools":
      return providerDetails.some(p => p.provider.tools);
    case "discounted":
      return providerDetails.some(p => p.provider.discount && parseFloat(p.provider.discount) > 0);
    default:
      return true;
  }
}
export function AllModels(t0) {
  const $ = _c(431);
  const {
    children,
    models,
    providers,
    title,
    description,
    categoryFilter
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const {
    t
  } = useI18n();
  const [viewMode, setViewMode] = useState(searchParams.get("view") as "table" | "grid" === "grid" ? "grid" : "table");
  let t1;
  let t2;
  if ($[0] !== isMobile || $[1] !== searchParams || $[2] !== viewMode) {
    t1 = () => {
      const viewParam = searchParams.get("view");
      if (!viewParam && isMobile && viewMode !== "grid") {
        setViewMode("grid");
      }
    };
    t2 = [isMobile, searchParams, viewMode];
    $[0] = isMobile;
    $[1] = searchParams;
    $[2] = viewMode;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [expandedRows, setExpandedRows] = useState(t3);
  const [copiedModel, setCopiedModel] = useState(null);
  let t4;
  if ($[6] !== searchParams) {
    t4 = searchParams.get("q") ?? "";
    $[6] = searchParams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [searchQuery, setSearchQuery] = useState(t4);
  let t5;
  if ($[8] !== searchParams) {
    t5 = searchParams.get("filters");
    $[8] = searchParams;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const [showFilters, setShowFilters] = useState(t5 === "1");
  let t6;
  if ($[10] !== searchParams) {
    t6 = searchParams.get("sortField") as SortField || null;
    $[10] = searchParams;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const [sortField, setSortField] = useState(t6);
  const [sortDirection, setSortDirection] = useState(searchParams.get("sortDir") as SortDirection === "desc" ? "desc" : "asc");
  let t7;
  if ($[12] !== searchParams) {
    t7 = searchParams.get("category") ?? "all";
    $[12] = searchParams;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== searchParams) {
    t8 = searchParams.get("streaming");
    $[14] = searchParams;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = t8 === "true";
  let t10;
  if ($[16] !== searchParams) {
    t10 = searchParams.get("vision");
    $[16] = searchParams;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = t10 === "true";
  let t12;
  if ($[18] !== searchParams) {
    t12 = searchParams.get("tools");
    $[18] = searchParams;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = t12 === "true";
  let t14;
  if ($[20] !== searchParams) {
    t14 = searchParams.get("reasoning");
    $[20] = searchParams;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = t14 === "true";
  let t16;
  if ($[22] !== searchParams) {
    t16 = searchParams.get("reasoningBudget");
    $[22] = searchParams;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = t16 === "true";
  let t18;
  if ($[24] !== searchParams) {
    t18 = searchParams.get("jsonOutput");
    $[24] = searchParams;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const t19 = t18 === "true";
  let t20;
  if ($[26] !== searchParams) {
    t20 = searchParams.get("jsonOutputSchema");
    $[26] = searchParams;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  const t21 = t20 === "true";
  let t22;
  if ($[28] !== searchParams) {
    t22 = searchParams.get("imageGeneration");
    $[28] = searchParams;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  const t23 = t22 === "true";
  let t24;
  if ($[30] !== searchParams) {
    t24 = searchParams.get("videoGeneration");
    $[30] = searchParams;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  const t25 = t24 === "true";
  let t26;
  if ($[32] !== searchParams) {
    t26 = searchParams.get("audioGeneration");
    $[32] = searchParams;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  const t27 = t26 === "true";
  let t28;
  if ($[34] !== searchParams) {
    t28 = searchParams.get("embedding");
    $[34] = searchParams;
    $[35] = t28;
  } else {
    t28 = $[35];
  }
  const t29 = t28 === "true";
  let t30;
  if ($[36] !== searchParams) {
    t30 = searchParams.get("webSearch");
    $[36] = searchParams;
    $[37] = t30;
  } else {
    t30 = $[37];
  }
  const t31 = t30 === "true";
  let t32;
  if ($[38] !== searchParams) {
    t32 = searchParams.get("free");
    $[38] = searchParams;
    $[39] = t32;
  } else {
    t32 = $[39];
  }
  const t33 = t32 === "true";
  let t34;
  if ($[40] !== searchParams) {
    t34 = searchParams.get("discounted");
    $[40] = searchParams;
    $[41] = t34;
  } else {
    t34 = $[41];
  }
  const t35 = t34 === "true";
  let t36;
  if ($[42] !== t11 || $[43] !== t13 || $[44] !== t15 || $[45] !== t17 || $[46] !== t19 || $[47] !== t21 || $[48] !== t23 || $[49] !== t25 || $[50] !== t27 || $[51] !== t29 || $[52] !== t31 || $[53] !== t33 || $[54] !== t35 || $[55] !== t9) {
    t36 = {
      streaming: t9,
      vision: t11,
      tools: t13,
      reasoning: t15,
      reasoningBudget: t17,
      jsonOutput: t19,
      jsonOutputSchema: t21,
      imageGeneration: t23,
      videoGeneration: t25,
      audioGeneration: t27,
      embedding: t29,
      webSearch: t31,
      free: t33,
      discounted: t35
    };
    $[42] = t11;
    $[43] = t13;
    $[44] = t15;
    $[45] = t17;
    $[46] = t19;
    $[47] = t21;
    $[48] = t23;
    $[49] = t25;
    $[50] = t27;
    $[51] = t29;
    $[52] = t31;
    $[53] = t33;
    $[54] = t35;
    $[55] = t9;
    $[56] = t36;
  } else {
    t36 = $[56];
  }
  let t37;
  if ($[57] !== searchParams) {
    t37 = searchParams.get("provider") ?? "all";
    $[57] = searchParams;
    $[58] = t37;
  } else {
    t37 = $[58];
  }
  let t38;
  if ($[59] !== searchParams) {
    t38 = searchParams.get("inputPriceMin") ?? "";
    $[59] = searchParams;
    $[60] = t38;
  } else {
    t38 = $[60];
  }
  let t39;
  if ($[61] !== searchParams) {
    t39 = searchParams.get("inputPriceMax") ?? "";
    $[61] = searchParams;
    $[62] = t39;
  } else {
    t39 = $[62];
  }
  let t40;
  if ($[63] !== t38 || $[64] !== t39) {
    t40 = {
      min: t38,
      max: t39
    };
    $[63] = t38;
    $[64] = t39;
    $[65] = t40;
  } else {
    t40 = $[65];
  }
  let t41;
  if ($[66] !== searchParams) {
    t41 = searchParams.get("outputPriceMin") ?? "";
    $[66] = searchParams;
    $[67] = t41;
  } else {
    t41 = $[67];
  }
  let t42;
  if ($[68] !== searchParams) {
    t42 = searchParams.get("outputPriceMax") ?? "";
    $[68] = searchParams;
    $[69] = t42;
  } else {
    t42 = $[69];
  }
  let t43;
  if ($[70] !== t41 || $[71] !== t42) {
    t43 = {
      min: t41,
      max: t42
    };
    $[70] = t41;
    $[71] = t42;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  let t44;
  if ($[73] !== searchParams) {
    t44 = searchParams.get("contextSizeMin") ?? "";
    $[73] = searchParams;
    $[74] = t44;
  } else {
    t44 = $[74];
  }
  let t45;
  if ($[75] !== searchParams) {
    t45 = searchParams.get("contextSizeMax") ?? "";
    $[75] = searchParams;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  let t46;
  if ($[77] !== t44 || $[78] !== t45) {
    t46 = {
      min: t44,
      max: t45
    };
    $[77] = t44;
    $[78] = t45;
    $[79] = t46;
  } else {
    t46 = $[79];
  }
  let t47;
  if ($[80] !== t36 || $[81] !== t37 || $[82] !== t40 || $[83] !== t43 || $[84] !== t46 || $[85] !== t7) {
    t47 = {
      category: t7,
      capabilities: t36,
      selectedProvider: t37,
      inputPrice: t40,
      outputPrice: t43,
      contextSize: t46
    };
    $[80] = t36;
    $[81] = t37;
    $[82] = t40;
    $[83] = t43;
    $[84] = t46;
    $[85] = t7;
    $[86] = t47;
  } else {
    t47 = $[86];
  }
  const [filters, setFilters] = useState(t47);
  let t48;
  if ($[87] !== router || $[88] !== searchParams) {
    t48 = newParams => {
      const params = new URLSearchParams(searchParams.toString());
      Object.entries(newParams).forEach(t49 => {
        const [key, value] = t49;
        if (value !== undefined && value !== "") {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      });
      router.replace(`?${params.toString()}`, {
        scroll: false
      });
    };
    $[87] = router;
    $[88] = searchParams;
    $[89] = t48;
  } else {
    t48 = $[89];
  }
  const updateUrlWithFilters = t48;
  let t49;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = new Date();
    $[90] = t49;
  } else {
    t49 = $[90];
  }
  const now = t49;
  let t50;
  if ($[91] !== models) {
    let t51;
    if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
      t51 = model => model.mappings.some(mapping => !mapping.deprecatedAt || new Date(mapping.deprecatedAt) > now);
      $[93] = t51;
    } else {
      t51 = $[93];
    }
    t50 = models.filter(t51);
    $[91] = models;
    $[92] = t50;
  } else {
    t50 = $[92];
  }
  const nonDeprecatedModelCount = t50.length;
  let t51;
  if ($[94] !== nonDeprecatedModelCount || $[95] !== providers.length) {
    t51 = {
      totalModelCount: nonDeprecatedModelCount,
      totalProviderCount: providers.length
    };
    $[94] = nonDeprecatedModelCount;
    $[95] = providers.length;
    $[96] = t51;
  } else {
    t51 = $[96];
  }
  const {
    totalModelCount,
    totalProviderCount
  } = t51;
  let t52;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = new Date();
    $[97] = t52;
  } else {
    t52 = $[97];
  }
  const now_0 = t52;
  let t53;
  if ($[98] !== categoryFilter || $[99] !== filters.capabilities || $[100] !== filters.category || $[101] !== filters.contextSize || $[102] !== filters.inputPrice || $[103] !== filters.outputPrice || $[104] !== filters.selectedProvider || $[105] !== models || $[106] !== providers || $[107] !== searchQuery || $[108] !== sortDirection || $[109] !== sortField) {
    let t54;
    if ($[111] !== providers) {
      t54 = model_0 => {
        const nonDeprecatedMappings = model_0.mappings.filter(mapping_0 => {
          if (!mapping_0.deprecatedAt) {
            return true;
          }
          return new Date(mapping_0.deprecatedAt) > now_0;
        });
        return {
          ...model_0,
          providerDetails: nonDeprecatedMappings.map(mapping_1 => ({
            provider: mapping_1,
            providerInfo: providers.find(p => p.id === mapping_1.providerId)
          }))
        };
      };
      $[111] = providers;
      $[112] = t54;
    } else {
      t54 = $[112];
    }
    const baseModels = models.map(t54).filter(_temp3);
    const preFilteredModels = categoryFilter ? baseModels.filter(model_2 => applyCategoryFilter(categoryFilter, model_2, model_2.providerDetails)) : baseModels;
    let t55;
    if ($[113] !== filters.capabilities || $[114] !== filters.category || $[115] !== filters.contextSize || $[116] !== filters.inputPrice || $[117] !== filters.outputPrice || $[118] !== filters.selectedProvider || $[119] !== searchQuery) {
      t55 = model_3 => {
        if (searchQuery) {
          const normalize = _temp4;
          const queryTokens = searchQuery.trim().toLowerCase().split(/\s+/).map(_temp5).filter(Boolean);
          const providerStrings = (model_3.providerDetails ?? []).flatMap(_temp6);
          const haystackParts = [model_3.name ?? "", model_3.id, model_3.family, ...(model_3.aliases ?? []), ...providerStrings];
          const haystack = normalize(haystackParts.join(" "));
          const normalizedQuery = normalize(searchQuery);
          const containsAllTokens = queryTokens.every(t_1 => haystack.includes(t_1));
          const containsPhrase = normalizedQuery ? haystack.includes(normalizedQuery) : true;
          if (!(containsAllTokens || containsPhrase)) {
            return false;
          }
        }
        if (filters.category && filters.category !== "all") {
          bb143: switch (filters.category) {
            case "code":
              {
                if (model_3.free) {
                  return false;
                }
                if (model_3.stability === "unstable" || model_3.stability === "experimental") {
                  return false;
                }
                const hasCodeCapabilities = model_3.providerDetails.some(_temp7);
                if (!hasCodeCapabilities) {
                  return false;
                }
                break bb143;
              }
            case "chat":
              {
                const hasStreaming = model_3.providerDetails.some(_temp8);
                if (!hasStreaming) {
                  return false;
                }
                break bb143;
              }
            case "reasoning":
              {
                const hasReasoning = model_3.providerDetails.some(_temp9);
                if (!hasReasoning) {
                  return false;
                }
                break bb143;
              }
            case "creative":
              {
                if (model_3.output?.includes("image")) {
                  return false;
                }
                const hasCreativeStreaming = model_3.providerDetails.some(_temp10);
                if (!hasCreativeStreaming) {
                  return false;
                }
                break bb143;
              }
            case "image":
              {
                if (!model_3.output?.includes("image")) {
                  return false;
                }
                break bb143;
              }
            case "multimodal":
              {
                const hasVision = model_3.providerDetails.some(_temp11);
                if (!hasVision) {
                  return false;
                }
              }
          }
        }
        if (filters.capabilities.streaming && !model_3.providerDetails.some(_temp12)) {
          return false;
        }
        if (filters.capabilities.vision && !model_3.providerDetails.some(_temp13)) {
          return false;
        }
        if (filters.capabilities.tools && !model_3.providerDetails.some(_temp14)) {
          return false;
        }
        if (filters.capabilities.reasoning && !model_3.providerDetails.some(_temp15)) {
          return false;
        }
        if (filters.capabilities.reasoningBudget && !model_3.providerDetails.some(_temp16)) {
          return false;
        }
        if (filters.capabilities.jsonOutput && !model_3.providerDetails.some(_temp17)) {
          return false;
        }
        if (filters.capabilities.jsonOutputSchema && !model_3.providerDetails.some(_temp18)) {
          return false;
        }
        if (filters.capabilities.imageGeneration && !model_3.output?.includes("image")) {
          return false;
        }
        if (filters.capabilities.videoGeneration && !model_3.output?.includes("video")) {
          return false;
        }
        if (filters.capabilities.audioGeneration && !model_3.output?.includes("audio")) {
          return false;
        }
        if (filters.capabilities.embedding && !model_3.output?.includes("embedding")) {
          return false;
        }
        if (filters.capabilities.webSearch && !model_3.providerDetails.some(_temp19)) {
          return false;
        }
        if (filters.capabilities.free) {
          const hasRequestPrice = model_3.providerDetails.some(_temp20);
          if (!model_3.free || hasRequestPrice) {
            return false;
          }
        }
        if (filters.capabilities.discounted && !model_3.providerDetails.some(_temp21)) {
          return false;
        }
        if (filters.selectedProvider && filters.selectedProvider !== "all") {
          const hasSelectedProvider = model_3.providerDetails.some(p_16 => p_16.provider.providerId === filters.selectedProvider);
          if (!hasSelectedProvider) {
            return false;
          }
        }
        const hasInputPrice = (min, max) => model_3.providerDetails.some(p_17 => {
          if (p_17.provider.inputPrice === null || p_17.provider.inputPrice === undefined) {
            return !min && !max;
          }
          const price = parseFloat(p_17.provider.inputPrice) * 1000000;
          const minPrice = min ? parseFloat(min) : 0;
          const maxPrice = max ? parseFloat(max) : Infinity;
          return price >= minPrice && price <= maxPrice;
        });
        const hasOutputPrice = (min_0, max_0) => model_3.providerDetails.some(p_18 => {
          if (p_18.provider.outputPrice === null || p_18.provider.outputPrice === undefined) {
            return !min_0 && !max_0;
          }
          const price_0 = parseFloat(p_18.provider.outputPrice) * 1000000;
          const minPrice_0 = min_0 ? parseFloat(min_0) : 0;
          const maxPrice_0 = max_0 ? parseFloat(max_0) : Infinity;
          return price_0 >= minPrice_0 && price_0 <= maxPrice_0;
        });
        const hasContextSize = (min_1, max_1) => model_3.providerDetails.some(p_19 => {
          if (p_19.provider.contextSize === null || p_19.provider.contextSize === undefined) {
            return !min_1 && !max_1;
          }
          const size = p_19.provider.contextSize;
          const minSize = min_1 ? parseInt(min_1, 10) : 0;
          const maxSize = max_1 ? parseInt(max_1, 10) : Infinity;
          return size >= minSize && size <= maxSize;
        });
        if ((filters.inputPrice.min || filters.inputPrice.max) && !hasInputPrice(filters.inputPrice.min, filters.inputPrice.max)) {
          return false;
        }
        if ((filters.outputPrice.min || filters.outputPrice.max) && !hasOutputPrice(filters.outputPrice.min, filters.outputPrice.max)) {
          return false;
        }
        if ((filters.contextSize.min || filters.contextSize.max) && !hasContextSize(filters.contextSize.min, filters.contextSize.max)) {
          return false;
        }
        return true;
      };
      $[113] = filters.capabilities;
      $[114] = filters.category;
      $[115] = filters.contextSize;
      $[116] = filters.inputPrice;
      $[117] = filters.outputPrice;
      $[118] = filters.selectedProvider;
      $[119] = searchQuery;
      $[120] = t55;
    } else {
      t55 = $[120];
    }
    const filteredModels = preFilteredModels.filter(t55);
    let t56;
    if ($[121] !== sortDirection || $[122] !== sortField) {
      t56 = (a, b) => {
        if (!sortField) {
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : a.releasedAt ? new Date(a.releasedAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : b.releasedAt ? new Date(b.releasedAt).getTime() : 0;
          return bDate - aDate;
        }
        let aValue;
        let bValue;
        bb533: switch (sortField) {
          case "provider":
            {
              aValue = (a.providerDetails[0]?.providerInfo?.name ?? a.providerDetails[0]?.provider.providerId ?? "").toLowerCase();
              bValue = (b.providerDetails[0]?.providerInfo?.name ?? b.providerDetails[0]?.provider.providerId ?? "").toLowerCase();
              break bb533;
            }
          case "name":
            {
              aValue = (a.name ?? a.id).toLowerCase();
              bValue = (b.name ?? b.id).toLowerCase();
              break bb533;
            }
          case "inputPrice":
            {
              const aInputPrices = a.providerDetails.map(_temp22).filter(_temp23).map(_temp24);
              const bInputPrices = b.providerDetails.map(_temp25).filter(_temp26).map(_temp27);
              aValue = aInputPrices.length > 0 ? Math.min(...aInputPrices) : Infinity;
              bValue = bInputPrices.length > 0 ? Math.min(...bInputPrices) : Infinity;
              break bb533;
            }
          case "outputPrice":
            {
              const aOutputPrices = a.providerDetails.map(_temp28).filter(_temp29).map(_temp30);
              const bOutputPrices = b.providerDetails.map(_temp31).filter(_temp32).map(_temp33);
              aValue = aOutputPrices.length > 0 ? Math.min(...aOutputPrices) : Infinity;
              bValue = bOutputPrices.length > 0 ? Math.min(...bOutputPrices) : Infinity;
              break bb533;
            }
          case "cachedInputPrice":
            {
              const aCachedInputPrices = a.providerDetails.map(_temp34).filter(_temp35).map(_temp36);
              const bCachedInputPrices = b.providerDetails.map(_temp37).filter(_temp38).map(_temp39);
              aValue = aCachedInputPrices.length > 0 ? Math.min(...aCachedInputPrices) : Infinity;
              bValue = bCachedInputPrices.length > 0 ? Math.min(...bCachedInputPrices) : Infinity;
              break bb533;
            }
          default:
            {
              return 0;
            }
        }
        if (aValue < bValue) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      };
      $[121] = sortDirection;
      $[122] = sortField;
      $[123] = t56;
    } else {
      t56 = $[123];
    }
    t53 = [...filteredModels].sort(t56);
    $[98] = categoryFilter;
    $[99] = filters.capabilities;
    $[100] = filters.category;
    $[101] = filters.contextSize;
    $[102] = filters.inputPrice;
    $[103] = filters.outputPrice;
    $[104] = filters.selectedProvider;
    $[105] = models;
    $[106] = providers;
    $[107] = searchQuery;
    $[108] = sortDirection;
    $[109] = sortField;
    $[110] = t53;
  } else {
    t53 = $[110];
  }
  const modelsWithProviders = t53;
  let t54;
  if ($[124] !== modelsWithProviders) {
    t54 = new Set(modelsWithProviders.flatMap(_temp41));
    $[124] = modelsWithProviders;
    $[125] = t54;
  } else {
    t54 = $[125];
  }
  const uniqueProviders = t54;
  const filteredProviderCount = uniqueProviders.size;
  let t55;
  if ($[126] !== filters.selectedProvider || $[127] !== modelsWithProviders || $[128] !== sortDirection || $[129] !== sortField || $[130] !== t) {
    const rows = [];
    const providerFilter = filters.selectedProvider && filters.selectedProvider !== "all" ? filters.selectedProvider : null;
    for (const model_5 of modelsWithProviders) {
      for (const {
        provider,
        providerInfo
      } of model_5.providerDetails) {
        if (providerFilter && provider.providerId !== providerFilter) {
          continue;
        }
        const hasAdditionalPricing = provider.webSearch ?? (provider.requestPrice !== null && provider.requestPrice !== undefined && parseFloat(provider.requestPrice) > 0) ?? (provider.perSecondPrice !== null && provider.perSecondPrice !== undefined);
        rows.push({
          model: model_5,
          provider,
          providerInfo,
          hasAdditionalPricing,
          rowKey: `${provider.providerId}-${model_5.id}-${provider.region ?? ""}`,
          capabilities: computeCapabilities(provider, model_5, t),
          ProviderIcon: getProviderIcon(provider.providerId)
        });
      }
    }
    let t56;
    if ($[132] !== sortDirection || $[133] !== sortField) {
      t56 = (a_0, b_0) => {
        if (!sortField) {
          const aDate_0 = a_0.model.createdAt ? new Date(a_0.model.createdAt).getTime() : a_0.model.releasedAt ? new Date(a_0.model.releasedAt).getTime() : 0;
          const bDate_0 = b_0.model.createdAt ? new Date(b_0.model.createdAt).getTime() : b_0.model.releasedAt ? new Date(b_0.model.releasedAt).getTime() : 0;
          return bDate_0 - aDate_0;
        }
        let aValue_0;
        let bValue_0;
        bb772: switch (sortField) {
          case "provider":
            {
              aValue_0 = (a_0.providerInfo?.name ?? a_0.provider.providerId).toLowerCase();
              bValue_0 = (b_0.providerInfo?.name ?? b_0.provider.providerId).toLowerCase();
              break bb772;
            }
          case "name":
            {
              aValue_0 = (a_0.model.name ?? a_0.model.id).toLowerCase();
              bValue_0 = (b_0.model.name ?? b_0.model.id).toLowerCase();
              break bb772;
            }
          case "inputPrice":
            {
              const aPrice_1 = a_0.provider.inputPrice;
              const bPrice_1 = b_0.provider.inputPrice;
              aValue_0 = aPrice_1 !== null && aPrice_1 !== undefined ? parseFloat(aPrice_1) : Infinity;
              bValue_0 = bPrice_1 !== null && bPrice_1 !== undefined ? parseFloat(bPrice_1) : Infinity;
              break bb772;
            }
          case "outputPrice":
            {
              const aPrice_0 = a_0.provider.outputPrice;
              const bPrice_0 = b_0.provider.outputPrice;
              aValue_0 = aPrice_0 !== null && aPrice_0 !== undefined ? parseFloat(aPrice_0) : Infinity;
              bValue_0 = bPrice_0 !== null && bPrice_0 !== undefined ? parseFloat(bPrice_0) : Infinity;
              break bb772;
            }
          case "cachedInputPrice":
            {
              const aPrice = a_0.provider.cachedInputPrice;
              const bPrice = b_0.provider.cachedInputPrice;
              aValue_0 = aPrice !== null && aPrice !== undefined ? parseFloat(aPrice) : Infinity;
              bValue_0 = bPrice !== null && bPrice !== undefined ? parseFloat(bPrice) : Infinity;
              break bb772;
            }
          default:
            {
              return 0;
            }
        }
        if (aValue_0 < bValue_0) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (aValue_0 > bValue_0) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      };
      $[132] = sortDirection;
      $[133] = sortField;
      $[134] = t56;
    } else {
      t56 = $[134];
    }
    t55 = rows.sort(t56);
    $[126] = filters.selectedProvider;
    $[127] = modelsWithProviders;
    $[128] = sortDirection;
    $[129] = sortField;
    $[130] = t;
    $[131] = t55;
  } else {
    t55 = $[131];
  }
  const flattenedRows = t55;
  let t56;
  if ($[135] !== filters.capabilities || $[136] !== filters.category || $[137] !== filters.contextSize.max || $[138] !== filters.contextSize.min || $[139] !== filters.inputPrice.max || $[140] !== filters.inputPrice.min || $[141] !== filters.outputPrice.max || $[142] !== filters.outputPrice.min || $[143] !== filters.selectedProvider || $[144] !== searchQuery) {
    t56 = searchQuery || filters.category && filters.category !== "all" || Object.values(filters.capabilities).some(Boolean) || filters.selectedProvider && filters.selectedProvider !== "all" || filters.inputPrice.min || filters.inputPrice.max || filters.outputPrice.min || filters.outputPrice.max || filters.contextSize.min || filters.contextSize.max;
    $[135] = filters.capabilities;
    $[136] = filters.category;
    $[137] = filters.contextSize.max;
    $[138] = filters.contextSize.min;
    $[139] = filters.inputPrice.max;
    $[140] = filters.inputPrice.min;
    $[141] = filters.outputPrice.max;
    $[142] = filters.outputPrice.min;
    $[143] = filters.selectedProvider;
    $[144] = searchQuery;
    $[145] = t56;
  } else {
    t56 = $[145];
  }
  const hasActiveFilters = t56;
  let t57;
  if ($[146] !== searchParams) {
    t57 = Number(searchParams.get("page")) || 1;
    $[146] = searchParams;
    $[147] = t57;
  } else {
    t57 = $[147];
  }
  const currentPage = Math.max(1, Math.floor(t57));
  const totalTablePages = Math.ceil(flattenedRows.length / MODELS_PER_PAGE);
  const totalGridPages = Math.ceil(modelsWithProviders.length / MODELS_PER_PAGE);
  const totalPages = viewMode === "table" ? totalTablePages : totalGridPages;
  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  let t58;
  let t59;
  if ($[148] !== currentPage || $[149] !== safePage || $[150] !== updateUrlWithFilters) {
    t58 = () => {
      if (currentPage !== safePage) {
        updateUrlWithFilters({
          page: safePage > 1 ? String(safePage) : undefined
        });
      }
    };
    t59 = [currentPage, safePage, updateUrlWithFilters];
    $[148] = currentPage;
    $[149] = safePage;
    $[150] = updateUrlWithFilters;
    $[151] = t58;
    $[152] = t59;
  } else {
    t58 = $[151];
    t59 = $[152];
  }
  useEffect(t58, t59);
  let T0;
  let goToPage;
  let t60;
  let t61;
  let t62;
  let t63;
  let t64;
  let t65;
  let t66;
  let t67;
  let t68;
  let t69;
  let t70;
  let t71;
  if ($[153] !== children || $[154] !== copiedModel || $[155] !== description || $[156] !== expandedRows || $[157] !== filteredProviderCount || $[158] !== filters.capabilities || $[159] !== filters.category || $[160] !== filters.contextSize.max || $[161] !== filters.contextSize.min || $[162] !== filters.inputPrice.max || $[163] !== filters.inputPrice.min || $[164] !== filters.outputPrice.max || $[165] !== filters.outputPrice.min || $[166] !== filters.selectedProvider || $[167] !== flattenedRows || $[168] !== hasActiveFilters || $[169] !== modelsWithProviders || $[170] !== pathname || $[171] !== providers || $[172] !== router || $[173] !== safePage || $[174] !== searchParams || $[175] !== searchQuery || $[176] !== showFilters || $[177] !== sortDirection || $[178] !== sortField || $[179] !== t || $[180] !== title || $[181] !== totalModelCount || $[182] !== totalPages || $[183] !== totalProviderCount || $[184] !== updateUrlWithFilters || $[185] !== viewMode) {
    const paginatedFlattenedRows = flattenedRows.slice((safePage - 1) * MODELS_PER_PAGE, safePage * MODELS_PER_PAGE);
    const paginatedModels = modelsWithProviders.slice((safePage - 1) * MODELS_PER_PAGE, safePage * MODELS_PER_PAGE);
    let t72;
    if ($[200] !== updateUrlWithFilters) {
      t72 = page => {
        updateUrlWithFilters({
          page: page > 1 ? String(page) : undefined
        });
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      };
      $[200] = updateUrlWithFilters;
      $[201] = t72;
    } else {
      t72 = $[201];
    }
    goToPage = t72;
    let t73;
    if ($[202] !== pathname || $[203] !== searchParams) {
      t73 = page_0 => {
        const params_0 = new URLSearchParams(searchParams.toString());
        if (page_0 > 1) {
          params_0.set("page", String(page_0));
        } else {
          params_0.delete("page");
        }
        const qs = params_0.toString();
        return `${pathname}${qs ? `?${qs}` : ""}`;
      };
      $[202] = pathname;
      $[203] = searchParams;
      $[204] = t73;
    } else {
      t73 = $[204];
    }
    const buildPageUrl = t73;
    let t74;
    if ($[205] === Symbol.for("react.memo_cache_sentinel")) {
      t74 = rowKey => {
        setExpandedRows(prev => {
          const next = new Set(prev);
          if (next.has(rowKey)) {
            next.delete(rowKey);
          } else {
            next.add(rowKey);
          }
          return next;
        });
      };
      $[205] = t74;
    } else {
      t74 = $[205];
    }
    const toggleRowExpanded = t74;
    let t75;
    if ($[206] === Symbol.for("react.memo_cache_sentinel")) {
      t75 = async (text, key_0, e) => {
        e.stopPropagation();
        ;
        try {
          await navigator.clipboard.writeText(text);
          setCopiedModel(key_0);
          setTimeout(() => setCopiedModel(null), 2000);
        } catch (t76) {
          const err = t76;
          console.error("Failed to copy text:", err);
        }
      };
      $[206] = t75;
    } else {
      t75 = $[206];
    }
    const copyToClipboard = t75;
    let t76;
    if ($[207] !== sortDirection || $[208] !== sortField || $[209] !== updateUrlWithFilters) {
      t76 = field => {
        if (sortField === field) {
          const newDir = sortDirection === "asc" ? "desc" : "asc";
          setSortDirection(newDir);
          updateUrlWithFilters({
            sortDir: newDir
          });
        } else {
          setSortField(field);
          setSortDirection("asc");
          updateUrlWithFilters({
            sortField: field,
            sortDir: "asc"
          });
        }
      };
      $[207] = sortDirection;
      $[208] = sortField;
      $[209] = updateUrlWithFilters;
      $[210] = t76;
    } else {
      t76 = $[210];
    }
    const handleSort = t76;
    let t77;
    if ($[211] !== sortDirection || $[212] !== sortField) {
      t77 = field_0 => {
        if (sortField !== field_0) {
          return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />;
        }
        return sortDirection === "asc" ? <ArrowUp className="ml-2 h-4 w-4 text-primary" /> : <ArrowDown className="ml-2 h-4 w-4 text-primary" />;
      };
      $[211] = sortDirection;
      $[212] = sortField;
      $[213] = t77;
    } else {
      t77 = $[213];
    }
    const getSortIcon = t77;
    const shouldShowStabilityWarning = _temp42;
    let t78;
    if ($[214] !== t) {
      t78 = (price_1, discount) => {
        if (price_1 === null || price_1 === undefined) {
          return "\u2014";
        }
        const priceNum = parseFloat(price_1);
        const discountNum = discount ? parseFloat(discount) : 0;
        const originalPrice = (priceNum * 1000000).toFixed(2);
        if (discountNum > 0) {
          const discountedPrice = (priceNum * 1000000 * (1 - discountNum)).toFixed(2);
          return <div className="flex flex-col items-end"><div className="flex items-center gap-1"><span className="line-through text-muted-foreground text-xs">${originalPrice}</span><span className="text-green-600 font-semibold">${discountedPrice}</span></div><span className="text-[10px] text-green-600">-{t("public.models.discount.off", {
                percent: Math.round(discountNum * 100)
              })}</span></div>;
        }
        return `$${originalPrice}`;
      };
      $[214] = t;
      $[215] = t78;
    } else {
      t78 = $[215];
    }
    const formatPrice = t78;
    let t79;
    if ($[216] !== t) {
      t79 = (provider_0, model_6) => {
        const capabilities = [];
        if (provider_0.streaming) {
          capabilities.push({
            icon: Zap,
            label: t("public.capabilities.streaming"),
            color: "text-blue-500"
          });
        }
        if (provider_0.vision) {
          capabilities.push({
            icon: Eye,
            label: t("public.capabilities.vision"),
            color: "text-green-500"
          });
        }
        if (provider_0.tools) {
          capabilities.push({
            icon: Wrench,
            label: t("public.capabilities.tools"),
            color: "text-purple-500"
          });
        }
        if (provider_0.reasoning) {
          capabilities.push({
            icon: MessageSquare,
            label: t("public.capabilities.reasoning"),
            color: "text-orange-500"
          });
        }
        if (provider_0.reasoningMaxTokens) {
          capabilities.push({
            icon: Sliders,
            label: t("public.capabilities.reasoningBudget"),
            color: "text-amber-500"
          });
        }
        if (provider_0.jsonOutput) {
          capabilities.push({
            icon: Braces,
            label: t("public.capabilities.jsonOutput"),
            color: "text-cyan-500"
          });
        }
        if (provider_0.jsonOutputSchema) {
          capabilities.push({
            icon: FileJson2,
            label: t("public.capabilities.structuredJsonOutput"),
            color: "text-teal-500"
          });
        }
        if (model_6?.output?.includes("image")) {
          capabilities.push({
            icon: ImagePlus,
            label: t("public.capabilities.imageGeneration"),
            color: "text-pink-500"
          });
        }
        if (model_6?.output?.includes("video")) {
          capabilities.push({
            icon: Video,
            label: t("public.capabilities.videoGeneration"),
            color: "text-violet-500"
          });
        }
        if (model_6?.output?.includes("audio")) {
          capabilities.push({
            icon: Volume2,
            label: t("public.capabilities.audioGeneration"),
            color: "text-rose-500"
          });
        }
        if (model_6?.output?.includes("embedding")) {
          capabilities.push({
            icon: Boxes,
            label: t("public.capabilities.embedding"),
            color: "text-indigo-500"
          });
        }
        if (provider_0.webSearch) {
          capabilities.push({
            icon: Globe,
            label: t("public.capabilities.nativeWebSearch"),
            color: "text-sky-500"
          });
        }
        return capabilities;
      };
      $[216] = t;
      $[217] = t79;
    } else {
      t79 = $[217];
    }
    const getCapabilityIcons = t79;
    let t80;
    if ($[218] !== updateUrlWithFilters) {
      t80 = () => {
        setSearchQuery("");
        setFilters({
          category: "all",
          capabilities: {
            streaming: false,
            vision: false,
            tools: false,
            reasoning: false,
            reasoningBudget: false,
            jsonOutput: false,
            jsonOutputSchema: false,
            imageGeneration: false,
            videoGeneration: false,
            audioGeneration: false,
            embedding: false,
            webSearch: false,
            free: false,
            discounted: false
          },
          selectedProvider: "all",
          inputPrice: {
            min: "",
            max: ""
          },
          outputPrice: {
            min: "",
            max: ""
          },
          contextSize: {
            min: "",
            max: ""
          }
        });
        setSortField(null);
        setSortDirection("asc");
        updateUrlWithFilters({
          q: undefined,
          category: undefined,
          streaming: undefined,
          vision: undefined,
          tools: undefined,
          reasoning: undefined,
          reasoningBudget: undefined,
          jsonOutput: undefined,
          jsonOutputSchema: undefined,
          imageGeneration: undefined,
          videoGeneration: undefined,
          audioGeneration: undefined,
          embedding: undefined,
          webSearch: undefined,
          free: undefined,
          discounted: undefined,
          provider: undefined,
          inputPriceMin: undefined,
          inputPriceMax: undefined,
          outputPriceMin: undefined,
          outputPriceMax: undefined,
          contextSizeMin: undefined,
          contextSizeMax: undefined,
          sortField: undefined,
          sortDir: undefined
        });
      };
      $[218] = updateUrlWithFilters;
      $[219] = t80;
    } else {
      t80 = $[219];
    }
    const clearFilters = t80;
    let t81;
    if ($[220] !== filters.capabilities || $[221] !== filters.category || $[222] !== filters.contextSize.max || $[223] !== filters.contextSize.min || $[224] !== filters.inputPrice.max || $[225] !== filters.inputPrice.min || $[226] !== filters.outputPrice.max || $[227] !== filters.outputPrice.min || $[228] !== filters.selectedProvider || $[229] !== providers || $[230] !== showFilters || $[231] !== t || $[232] !== updateUrlWithFilters) {
      t81 = () => <Card className={`transition-all duration-200 ${showFilters ? "opacity-100" : "opacity-0 hidden"}`}><CardContent className="pt-6"><div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"><div className="space-y-3"><h3 className="font-medium text-sm">{t("public.models.list.useCase")}</h3><Select value={filters.category} onValueChange={value_0 => {
                setFilters(prev_0 => ({
                  ...prev_0,
                  category: value_0
                }));
                updateUrlWithFilters({
                  category: value_0 !== "all" ? value_0 : undefined
                });
              }}><SelectTrigger className="w-full"><SelectValue placeholder={t("public.models.list.allUseCases")} /></SelectTrigger><SelectContent><SelectItem value="all"><div className="flex items-center gap-2"><List className="h-4 w-4 text-muted-foreground" />{t("public.models.list.allUseCases")}</div></SelectItem><SelectItem value="code"><div className="flex items-center gap-2"><Code className="h-4 w-4 text-indigo-500" />{t("public.models.list.codeGeneration")}</div></SelectItem><SelectItem value="chat"><div className="flex items-center gap-2"><Bot className="h-4 w-4 text-blue-500" />{t("public.models.list.chatAssistants")}</div></SelectItem><SelectItem value="reasoning"><div className="flex items-center gap-2"><Brain className="h-4 w-4 text-orange-500" />{t("public.models.list.reasoningAnalysis")}</div></SelectItem><SelectItem value="creative"><div className="flex items-center gap-2"><PenTool className="h-4 w-4 text-purple-500" />{t("public.models.list.creativeWriting")}</div></SelectItem><SelectItem value="image"><div className="flex items-center gap-2"><ImagePlus className="h-4 w-4 text-pink-500" />{t("public.models.list.imageGeneration")}</div></SelectItem><SelectItem value="multimodal"><div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" />{t("public.models.list.multimodalVision")}</div></SelectItem></SelectContent></Select></div><div className="space-y-3"><h3 className="font-medium text-sm">{t("public.common.capabilities")}</h3><div className="flex flex-wrap gap-2">{[{
                  key: "streaming",
                  label: t("public.capabilities.streaming"),
                  icon: Zap,
                  color: "text-blue-500"
                }, {
                  key: "vision",
                  label: t("public.capabilities.vision"),
                  icon: Eye,
                  color: "text-green-500"
                }, {
                  key: "tools",
                  label: t("public.capabilities.tools"),
                  icon: Wrench,
                  color: "text-purple-500"
                }, {
                  key: "reasoning",
                  label: t("public.capabilities.reasoning"),
                  icon: MessageSquare,
                  color: "text-orange-500"
                }, {
                  key: "reasoningBudget",
                  label: t("public.capabilities.reasoningBudget"),
                  icon: Sliders,
                  color: "text-amber-500"
                }, {
                  key: "jsonOutput",
                  label: t("public.capabilities.json"),
                  icon: Braces,
                  color: "text-cyan-500"
                }, {
                  key: "jsonOutputSchema",
                  label: t("public.capabilities.structuredJson"),
                  icon: FileJson2,
                  color: "text-teal-500"
                }, {
                  key: "imageGeneration",
                  label: t("public.capabilities.imageGeneration"),
                  icon: ImagePlus,
                  color: "text-pink-500"
                }, {
                  key: "videoGeneration",
                  label: t("public.capabilities.videoGen"),
                  icon: Video,
                  color: "text-violet-500"
                }, {
                  key: "audioGeneration",
                  label: t("public.capabilities.speechGen"),
                  icon: Volume2,
                  color: "text-rose-500"
                }, {
                  key: "embedding",
                  label: t("public.capabilities.embedding"),
                  icon: Boxes,
                  color: "text-indigo-500"
                }, {
                  key: "webSearch",
                  label: t("public.capabilities.webSearch"),
                  icon: Globe,
                  color: "text-sky-500"
                }, {
                  key: "free",
                  label: t("public.common.free"),
                  icon: Gift,
                  color: "text-emerald-500"
                }, {
                  key: "discounted",
                  label: t("public.models.categories.discounted.title"),
                  icon: Percent,
                  color: "text-red-500"
                }].map(t82 => {
                  const {
                    key: key_1,
                    label,
                    icon: Icon,
                    color
                  } = t82;
                  return <Toggle key={`${key_1}-${label}`} variant="outline" size="sm" pressed={filters.capabilities[key_1 as keyof typeof filters.capabilities]} onPressedChange={pressed => {
                    setFilters(prev_1 => ({
                      ...prev_1,
                      capabilities: {
                        ...prev_1.capabilities,
                        [key_1]: pressed
                      }
                    }));
                    updateUrlWithFilters({
                      [key_1]: pressed ? "true" : undefined
                    });
                  }} className="gap-1.5"><Icon className={`h-3.5 w-3.5 ${color}`} /><span className="text-xs">{label}</span></Toggle>;
                })}</div></div><div className="space-y-3"><h3 className="font-medium text-sm">{t("public.models.list.provider")}</h3><Select value={filters.selectedProvider} onValueChange={value_1 => {
                setFilters(prev_2 => ({
                  ...prev_2,
                  selectedProvider: value_1
                }));
                updateUrlWithFilters({
                  provider: value_1 === "all" ? undefined : value_1
                });
              }}><SelectTrigger className="w-full"><SelectValue placeholder={t("public.models.list.allProviders")} /></SelectTrigger><SelectContent><SelectItem value="all">{t("public.models.list.allProviders")}</SelectItem>{providers.map(_temp43)}</SelectContent></Select></div><div className="space-y-3"><h3 className="font-medium text-sm">{t("public.models.list.inputPrice")}</h3><div className="space-y-2"><Input placeholder={t("public.models.list.minPrice")} type="number" value={filters.inputPrice.min} onChange={e_0 => {
                  const value_2 = e_0.target.value;
                  setFilters(prev_3 => ({
                    ...prev_3,
                    inputPrice: {
                      ...prev_3.inputPrice,
                      min: value_2
                    }
                  }));
                  updateUrlWithFilters({
                    inputPriceMin: value_2 ?? undefined
                  });
                }} className="h-8" /><Input placeholder={t("public.models.list.maxPrice")} type="number" value={filters.inputPrice.max} onChange={e_1 => {
                  const value_3 = e_1.target.value;
                  setFilters(prev_4 => ({
                    ...prev_4,
                    inputPrice: {
                      ...prev_4.inputPrice,
                      max: value_3
                    }
                  }));
                  updateUrlWithFilters({
                    inputPriceMax: value_3 ?? undefined
                  });
                }} className="h-8" /></div></div><div className="space-y-3"><h3 className="font-medium text-sm">{t("public.models.list.outputPrice")}</h3><div className="space-y-2"><Input placeholder={t("public.models.list.minPrice")} type="number" value={filters.outputPrice.min} onChange={e_2 => {
                  const value_4 = e_2.target.value;
                  setFilters(prev_5 => ({
                    ...prev_5,
                    outputPrice: {
                      ...prev_5.outputPrice,
                      min: value_4
                    }
                  }));
                  updateUrlWithFilters({
                    outputPriceMin: value_4 ?? undefined
                  });
                }} className="h-8" /><Input placeholder={t("public.models.list.maxPrice")} type="number" value={filters.outputPrice.max} onChange={e_3 => {
                  const value_5 = e_3.target.value;
                  setFilters(prev_6 => ({
                    ...prev_6,
                    outputPrice: {
                      ...prev_6.outputPrice,
                      max: value_5
                    }
                  }));
                  updateUrlWithFilters({
                    outputPriceMax: value_5 ?? undefined
                  });
                }} className="h-8" /></div></div><div className="space-y-3"><h3 className="font-medium text-sm">{t("public.models.list.contextSize")}</h3><div className="space-y-2"><Input placeholder={t("public.models.list.minSize")} type="number" value={filters.contextSize.min} onChange={e_4 => {
                  const value_6 = e_4.target.value;
                  setFilters(prev_7 => ({
                    ...prev_7,
                    contextSize: {
                      ...prev_7.contextSize,
                      min: value_6
                    }
                  }));
                  updateUrlWithFilters({
                    contextSizeMin: value_6 ?? undefined
                  });
                }} className="h-8" /><Input placeholder={t("public.models.list.maxSize")} type="number" value={filters.contextSize.max} onChange={e_5 => {
                  const value_7 = e_5.target.value;
                  setFilters(prev_8 => ({
                    ...prev_8,
                    contextSize: {
                      ...prev_8.contextSize,
                      max: value_7
                    }
                  }));
                  updateUrlWithFilters({
                    contextSizeMax: value_7 ?? undefined
                  });
                }} className="h-8" /></div></div></div></CardContent></Card>;
      $[220] = filters.capabilities;
      $[221] = filters.category;
      $[222] = filters.contextSize.max;
      $[223] = filters.contextSize.min;
      $[224] = filters.inputPrice.max;
      $[225] = filters.inputPrice.min;
      $[226] = filters.outputPrice.max;
      $[227] = filters.outputPrice.min;
      $[228] = filters.selectedProvider;
      $[229] = providers;
      $[230] = showFilters;
      $[231] = t;
      $[232] = updateUrlWithFilters;
      $[233] = t81;
    } else {
      t81 = $[233];
    }
    const renderFilters = t81;
    const renderTableView = () => <div className="rounded-md border"><div className="relative w-full overflow-x-auto"><Table><TableHeader className="top-0 z-10 bg-background/95 backdrop-blur"><TableRow><TableHead className="w-[180px] bg-background/95 backdrop-blur-sm border-b"><Button variant="ghost" onClick={() => handleSort("provider")} className="h-auto p-0 font-semibold hover:bg-transparent justify-start uppercase text-xs tracking-wider">{t("public.common.provider")}{getSortIcon("provider")}</Button></TableHead><TableHead className="w-[280px] bg-background/95 backdrop-blur-sm border-b"><Button variant="ghost" onClick={() => handleSort("name")} className="h-auto p-0 font-semibold hover:bg-transparent justify-start uppercase text-xs tracking-wider">{t("public.common.modelId")}{getSortIcon("name")}</Button></TableHead><TableHead className="text-right bg-background/95 backdrop-blur-sm border-b"><Button variant="ghost" onClick={() => handleSort("inputPrice")} className="h-auto p-0 font-semibold hover:bg-transparent uppercase text-xs tracking-wider">{t("public.common.input")} $/M{getSortIcon("inputPrice")}</Button></TableHead><TableHead className="text-right bg-background/95 backdrop-blur-sm border-b"><Button variant="ghost" onClick={() => handleSort("outputPrice")} className="h-auto p-0 font-semibold hover:bg-transparent uppercase text-xs tracking-wider">{t("public.common.output")} $/M{getSortIcon("outputPrice")}</Button></TableHead><TableHead className="text-right bg-background/95 backdrop-blur-sm border-b"><Button variant="ghost" onClick={() => handleSort("cachedInputPrice")} className="h-auto p-0 font-semibold hover:bg-transparent uppercase text-xs tracking-wider">{t("public.common.cacheRead")} $/M{getSortIcon("cachedInputPrice")}</Button></TableHead><TableHead className="text-center bg-background/95 backdrop-blur-sm border-b uppercase text-xs tracking-wider font-semibold">{t("public.common.features")}</TableHead></TableRow></TableHeader><TableBody>{paginatedFlattenedRows.map(row => <ModelTableRow key={row.rowKey} row={row} isExpanded={expandedRows.has(row.rowKey)} copiedModel={copiedModel} onToggleExpand={() => toggleRowExpanded(row.rowKey)} onCopy={copyToClipboard} onNavigate={() => router.push(`/models/${encodeURIComponent(row.model.id)}/${row.provider.providerId}`)} formatPrice={formatPrice} t={t} />)}</TableBody></Table></div></div>;
    const renderGridView = () => <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{paginatedModels.map(model_7 => <ModelCard key={`${model_7.id}-${model_7.providerDetails[0].provider.providerId}-${model_7.providerDetails[0].provider.region ?? ""}`} shouldShowStabilityWarning={shouldShowStabilityWarning} getCapabilityIcons={getCapabilityIcons} model={model_7} goToModel={() => router.push(`/models/${encodeURIComponent(model_7.id)}`)} formatPrice={formatPrice} />)}</div>;
    t70 = "min-h-screen text-foreground bg-background";
    if ($[234] !== buildPageUrl || $[235] !== safePage || $[236] !== totalPages) {
      t71 = totalPages > 1 && <>{safePage > 1 && <link rel="prev" href={buildPageUrl(safePage - 1)} />}{safePage < totalPages && <link rel="next" href={buildPageUrl(safePage + 1)} />}</>;
      $[234] = buildPageUrl;
      $[235] = safePage;
      $[236] = totalPages;
      $[237] = t71;
    } else {
      t71 = $[237];
    }
    t69 = children;
    if ($[238] !== children) {
      t68 = cn("container mx-auto px-4 pb-8 space-y-6", {
        "pt-40": children
      });
      $[238] = children;
      $[239] = t68;
    } else {
      t68 = $[239];
    }
    T0 = TooltipProvider;
    t66 = 300;
    t67 = 100;
    t60 = "container mx-auto py-8 space-y-6";
    let t82;
    if ($[240] !== t || $[241] !== title) {
      t82 = title ?? t("public.models.list.defaultTitle");
      $[240] = t;
      $[241] = title;
      $[242] = t82;
    } else {
      t82 = $[242];
    }
    let t83;
    if ($[243] !== t82) {
      t83 = <h1 className="text-3xl font-bold">{t82}</h1>;
      $[243] = t82;
      $[244] = t83;
    } else {
      t83 = $[244];
    }
    let t84;
    if ($[245] !== description || $[246] !== t) {
      t84 = description ?? t("public.models.list.defaultDescription");
      $[245] = description;
      $[246] = t;
      $[247] = t84;
    } else {
      t84 = $[247];
    }
    let t85;
    if ($[248] !== t84) {
      t85 = <p className="text-muted-foreground mt-2">{t84}</p>;
      $[248] = t84;
      $[249] = t85;
    } else {
      t85 = $[249];
    }
    let t86;
    if ($[250] !== t83 || $[251] !== t85) {
      t86 = <div>{t83}{t85}</div>;
      $[250] = t83;
      $[251] = t85;
      $[252] = t86;
    } else {
      t86 = $[252];
    }
    let t87;
    if ($[253] === Symbol.for("react.memo_cache_sentinel")) {
      t87 = <ExternalLink className="h-4 w-4 mr-1" />;
      $[253] = t87;
    } else {
      t87 = $[253];
    }
    let t88;
    if ($[254] !== t) {
      t88 = t("public.common.apiDocs");
      $[254] = t;
      $[255] = t88;
    } else {
      t88 = $[255];
    }
    let t89;
    if ($[256] !== t88) {
      t89 = <Link href="https://docs.doteb.com/v1_models" target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm">{t87}{t88}</Button></Link>;
      $[256] = t88;
      $[257] = t89;
    } else {
      t89 = $[257];
    }
    let t90;
    if ($[258] === Symbol.for("react.memo_cache_sentinel")) {
      t90 = <Scale className="h-4 w-4 mr-1" />;
      $[258] = t90;
    } else {
      t90 = $[258];
    }
    let t91;
    if ($[259] !== t) {
      t91 = t("public.models.compare.title");
      $[259] = t;
      $[260] = t91;
    } else {
      t91 = $[260];
    }
    let t92;
    if ($[261] !== t91) {
      t92 = <Button size="sm" asChild={true}><Link href="/models/compare">{t90}{t91}</Link></Button>;
      $[261] = t91;
      $[262] = t92;
    } else {
      t92 = $[262];
    }
    let t93;
    if ($[263] !== t89 || $[264] !== t92) {
      t93 = <div className="flex items-center gap-2">{t89}{t92}</div>;
      $[263] = t89;
      $[264] = t92;
      $[265] = t93;
    } else {
      t93 = $[265];
    }
    if ($[266] !== t86 || $[267] !== t93) {
      t61 = <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-4">{t86}{t93}</div>;
      $[266] = t86;
      $[267] = t93;
      $[268] = t61;
    } else {
      t61 = $[268];
    }
    let t94;
    if ($[269] === Symbol.for("react.memo_cache_sentinel")) {
      t94 = <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />;
      $[269] = t94;
    } else {
      t94 = $[269];
    }
    let t95;
    if ($[270] !== t) {
      t95 = t("public.models.list.searchPlaceholder");
      $[270] = t;
      $[271] = t95;
    } else {
      t95 = $[271];
    }
    let t96;
    if ($[272] !== updateUrlWithFilters) {
      t96 = e_6 => {
        const value_8 = e_6.target.value;
        setSearchQuery(value_8);
        updateUrlWithFilters({
          q: value_8 ?? undefined
        });
      };
      $[272] = updateUrlWithFilters;
      $[273] = t96;
    } else {
      t96 = $[273];
    }
    let t97;
    if ($[274] !== searchQuery || $[275] !== t95 || $[276] !== t96) {
      t97 = <div className="relative flex-1 max-w-sm">{t94}<Input placeholder={t95} value={searchQuery} onChange={t96} className="pl-8" /></div>;
      $[274] = searchQuery;
      $[275] = t95;
      $[276] = t96;
      $[277] = t97;
    } else {
      t97 = $[277];
    }
    let t98;
    if ($[278] !== showFilters || $[279] !== updateUrlWithFilters) {
      t98 = () => {
        const next_0 = !showFilters;
        setShowFilters(next_0);
        updateUrlWithFilters({
          filters: next_0 ? "1" : undefined
        });
      };
      $[278] = showFilters;
      $[279] = updateUrlWithFilters;
      $[280] = t98;
    } else {
      t98 = $[280];
    }
    const t99 = hasActiveFilters ? "border-primary text-primary" : "";
    let t100;
    if ($[281] === Symbol.for("react.memo_cache_sentinel")) {
      t100 = <Filter className="h-4 w-4 mr-1" />;
      $[281] = t100;
    } else {
      t100 = $[281];
    }
    let t101;
    if ($[282] !== t) {
      t101 = t("public.common.filters");
      $[282] = t;
      $[283] = t101;
    } else {
      t101 = $[283];
    }
    let t102;
    if ($[284] !== filters.capabilities || $[285] !== filters.contextSize.max || $[286] !== filters.contextSize.min || $[287] !== filters.inputPrice.max || $[288] !== filters.inputPrice.min || $[289] !== filters.outputPrice.max || $[290] !== filters.outputPrice.min || $[291] !== hasActiveFilters || $[292] !== searchQuery) {
      t102 = hasActiveFilters && <Badge variant="secondary" className="ml-2 px-1 py-0 text-xs">{[searchQuery ? 1 : 0, Object.values(filters.capabilities).filter(Boolean).length, [filters.inputPrice.min, filters.inputPrice.max].filter(Boolean).length, [filters.outputPrice.min, filters.outputPrice.max].filter(Boolean).length, [filters.contextSize.min, filters.contextSize.max].filter(Boolean).length].reduce(_temp44, 0)}</Badge>;
      $[284] = filters.capabilities;
      $[285] = filters.contextSize.max;
      $[286] = filters.contextSize.min;
      $[287] = filters.inputPrice.max;
      $[288] = filters.inputPrice.min;
      $[289] = filters.outputPrice.max;
      $[290] = filters.outputPrice.min;
      $[291] = hasActiveFilters;
      $[292] = searchQuery;
      $[293] = t102;
    } else {
      t102 = $[293];
    }
    let t103;
    if ($[294] !== t101 || $[295] !== t102 || $[296] !== t98 || $[297] !== t99) {
      t103 = <Button variant="outline" size="sm" onClick={t98} className={t99}>{t100}{t101}{t102}</Button>;
      $[294] = t101;
      $[295] = t102;
      $[296] = t98;
      $[297] = t99;
      $[298] = t103;
    } else {
      t103 = $[298];
    }
    let t104;
    if ($[299] !== clearFilters || $[300] !== hasActiveFilters || $[301] !== t) {
      t104 = hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" />{t("public.common.clear")}</Button>;
      $[299] = clearFilters;
      $[300] = hasActiveFilters;
      $[301] = t;
      $[302] = t104;
    } else {
      t104 = $[302];
    }
    let t105;
    if ($[303] !== t103 || $[304] !== t104 || $[305] !== t97) {
      t105 = <div className="flex items-center gap-4">{t97}{t103}{t104}</div>;
      $[303] = t103;
      $[304] = t104;
      $[305] = t97;
      $[306] = t105;
    } else {
      t105 = $[306];
    }
    let t106;
    if ($[307] !== renderFilters) {
      t106 = renderFilters();
      $[307] = renderFilters;
      $[308] = t106;
    } else {
      t106 = $[308];
    }
    if ($[309] !== t105 || $[310] !== t106) {
      t62 = <div className="flex flex-col gap-4">{t105}{t106}</div>;
      $[309] = t105;
      $[310] = t106;
      $[311] = t62;
    } else {
      t62 = $[311];
    }
    const t107 = hasActiveFilters ? `${modelsWithProviders.length}/${totalModelCount}` : modelsWithProviders.length;
    let t108;
    if ($[312] !== t107) {
      t108 = <div className="text-2xl font-bold">{t107}</div>;
      $[312] = t107;
      $[313] = t108;
    } else {
      t108 = $[313];
    }
    let t109;
    if ($[314] !== t) {
      t109 = t("public.models.list.models");
      $[314] = t;
      $[315] = t109;
    } else {
      t109 = $[315];
    }
    let t110;
    if ($[316] !== t109) {
      t110 = <div className="text-sm text-muted-foreground">{t109}</div>;
      $[316] = t109;
      $[317] = t110;
    } else {
      t110 = $[317];
    }
    let t111;
    if ($[318] !== t108 || $[319] !== t110) {
      t111 = <Card><CardContent>{t108}{t110}</CardContent></Card>;
      $[318] = t108;
      $[319] = t110;
      $[320] = t111;
    } else {
      t111 = $[320];
    }
    const t112 = hasActiveFilters ? `${filteredProviderCount}/${totalProviderCount}` : totalProviderCount;
    let t113;
    if ($[321] !== t112) {
      t113 = <div className="text-2xl font-bold">{t112}</div>;
      $[321] = t112;
      $[322] = t113;
    } else {
      t113 = $[322];
    }
    let t114;
    if ($[323] !== t) {
      t114 = t("public.models.list.providers");
      $[323] = t;
      $[324] = t114;
    } else {
      t114 = $[324];
    }
    let t115;
    if ($[325] !== t114) {
      t115 = <div className="text-sm text-muted-foreground">{t114}</div>;
      $[325] = t114;
      $[326] = t115;
    } else {
      t115 = $[326];
    }
    let t116;
    if ($[327] !== t113 || $[328] !== t115) {
      t116 = <Card><CardContent>{t113}{t115}</CardContent></Card>;
      $[327] = t113;
      $[328] = t115;
      $[329] = t116;
    } else {
      t116 = $[329];
    }
    let t117;
    if ($[330] !== modelsWithProviders) {
      t117 = modelsWithProviders.filter(_temp46);
      $[330] = modelsWithProviders;
      $[331] = t117;
    } else {
      t117 = $[331];
    }
    let t118;
    if ($[332] !== t117.length) {
      t118 = <div className="text-2xl font-bold">{t117.length}</div>;
      $[332] = t117.length;
      $[333] = t118;
    } else {
      t118 = $[333];
    }
    let t119;
    if ($[334] !== t) {
      t119 = t("public.models.list.visionModels");
      $[334] = t;
      $[335] = t119;
    } else {
      t119 = $[335];
    }
    let t120;
    if ($[336] !== hasActiveFilters || $[337] !== t) {
      t120 = hasActiveFilters ? ` (${t("public.common.filtered")})` : "";
      $[336] = hasActiveFilters;
      $[337] = t;
      $[338] = t120;
    } else {
      t120 = $[338];
    }
    let t121;
    if ($[339] !== t119 || $[340] !== t120) {
      t121 = <div className="text-sm text-muted-foreground">{t119}{t120}</div>;
      $[339] = t119;
      $[340] = t120;
      $[341] = t121;
    } else {
      t121 = $[341];
    }
    let t122;
    if ($[342] !== t118 || $[343] !== t121) {
      t122 = <Card><CardContent>{t118}{t121}</CardContent></Card>;
      $[342] = t118;
      $[343] = t121;
      $[344] = t122;
    } else {
      t122 = $[344];
    }
    let t123;
    if ($[345] !== modelsWithProviders) {
      t123 = modelsWithProviders.filter(_temp48);
      $[345] = modelsWithProviders;
      $[346] = t123;
    } else {
      t123 = $[346];
    }
    let t124;
    if ($[347] !== t123.length) {
      t124 = <div className="text-2xl font-bold">{t123.length}</div>;
      $[347] = t123.length;
      $[348] = t124;
    } else {
      t124 = $[348];
    }
    let t125;
    if ($[349] !== t) {
      t125 = t("public.models.list.toolEnabled");
      $[349] = t;
      $[350] = t125;
    } else {
      t125 = $[350];
    }
    let t126;
    if ($[351] !== hasActiveFilters || $[352] !== t) {
      t126 = hasActiveFilters ? ` (${t("public.common.filtered")})` : "";
      $[351] = hasActiveFilters;
      $[352] = t;
      $[353] = t126;
    } else {
      t126 = $[353];
    }
    let t127;
    if ($[354] !== t125 || $[355] !== t126) {
      t127 = <div className="text-sm text-muted-foreground">{t125}{t126}</div>;
      $[354] = t125;
      $[355] = t126;
      $[356] = t127;
    } else {
      t127 = $[356];
    }
    let t128;
    if ($[357] !== t124 || $[358] !== t127) {
      t128 = <Card><CardContent>{t124}{t127}</CardContent></Card>;
      $[357] = t124;
      $[358] = t127;
      $[359] = t128;
    } else {
      t128 = $[359];
    }
    let t129;
    if ($[360] !== modelsWithProviders) {
      t129 = modelsWithProviders.filter(_temp49);
      $[360] = modelsWithProviders;
      $[361] = t129;
    } else {
      t129 = $[361];
    }
    let t130;
    if ($[362] !== t129.length) {
      t130 = <div className="text-2xl font-bold">{t129.length}</div>;
      $[362] = t129.length;
      $[363] = t130;
    } else {
      t130 = $[363];
    }
    let t131;
    if ($[364] !== t) {
      t131 = t("public.models.list.freeModels");
      $[364] = t;
      $[365] = t131;
    } else {
      t131 = $[365];
    }
    let t132;
    if ($[366] !== hasActiveFilters || $[367] !== t) {
      t132 = hasActiveFilters ? ` (${t("public.common.filtered")})` : "";
      $[366] = hasActiveFilters;
      $[367] = t;
      $[368] = t132;
    } else {
      t132 = $[368];
    }
    let t133;
    if ($[369] !== t131 || $[370] !== t132) {
      t133 = <div className="text-sm text-muted-foreground">{t131}{t132}</div>;
      $[369] = t131;
      $[370] = t132;
      $[371] = t133;
    } else {
      t133 = $[371];
    }
    let t134;
    if ($[372] !== t130 || $[373] !== t133) {
      t134 = <Card><CardContent>{t130}{t133}</CardContent></Card>;
      $[372] = t130;
      $[373] = t133;
      $[374] = t134;
    } else {
      t134 = $[374];
    }
    if ($[375] !== t111 || $[376] !== t116 || $[377] !== t122 || $[378] !== t128 || $[379] !== t134) {
      t63 = <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">{t111}{t116}{t122}{t128}{t134}</div>;
      $[375] = t111;
      $[376] = t116;
      $[377] = t122;
      $[378] = t128;
      $[379] = t134;
      $[380] = t63;
    } else {
      t63 = $[380];
    }
    const t135 = viewMode === "table" ? "default" : "outline";
    let t136;
    if ($[381] !== updateUrlWithFilters) {
      t136 = () => {
        setViewMode("table");
        updateUrlWithFilters({
          view: "table"
        });
      };
      $[381] = updateUrlWithFilters;
      $[382] = t136;
    } else {
      t136 = $[382];
    }
    let t137;
    if ($[383] === Symbol.for("react.memo_cache_sentinel")) {
      t137 = <List className="h-4 w-4 mr-1" />;
      $[383] = t137;
    } else {
      t137 = $[383];
    }
    let t138;
    if ($[384] !== t) {
      t138 = t("public.common.table");
      $[384] = t;
      $[385] = t138;
    } else {
      t138 = $[385];
    }
    let t139;
    if ($[386] !== t135 || $[387] !== t136 || $[388] !== t138) {
      t139 = <Button variant={t135} size="sm" onClick={t136}>{t137}{t138}</Button>;
      $[386] = t135;
      $[387] = t136;
      $[388] = t138;
      $[389] = t139;
    } else {
      t139 = $[389];
    }
    const t140 = viewMode === "grid" ? "default" : "outline";
    let t141;
    if ($[390] !== updateUrlWithFilters) {
      t141 = () => {
        setViewMode("grid");
        updateUrlWithFilters({
          view: "grid"
        });
      };
      $[390] = updateUrlWithFilters;
      $[391] = t141;
    } else {
      t141 = $[391];
    }
    let t142;
    if ($[392] === Symbol.for("react.memo_cache_sentinel")) {
      t142 = <Grid className="h-4 w-4 mr-1" />;
      $[392] = t142;
    } else {
      t142 = $[392];
    }
    let t143;
    if ($[393] !== t) {
      t143 = t("public.common.grid");
      $[393] = t;
      $[394] = t143;
    } else {
      t143 = $[394];
    }
    let t144;
    if ($[395] !== t140 || $[396] !== t141 || $[397] !== t143) {
      t144 = <Button variant={t140} size="sm" onClick={t141}>{t142}{t143}</Button>;
      $[395] = t140;
      $[396] = t141;
      $[397] = t143;
      $[398] = t144;
    } else {
      t144 = $[398];
    }
    if ($[399] !== t139 || $[400] !== t144) {
      t64 = <div className="flex items-center gap-2">{t139}{t144}</div>;
      $[399] = t139;
      $[400] = t144;
      $[401] = t64;
    } else {
      t64 = $[401];
    }
    t65 = viewMode === "table" ? renderTableView() : renderGridView();
    $[153] = children;
    $[154] = copiedModel;
    $[155] = description;
    $[156] = expandedRows;
    $[157] = filteredProviderCount;
    $[158] = filters.capabilities;
    $[159] = filters.category;
    $[160] = filters.contextSize.max;
    $[161] = filters.contextSize.min;
    $[162] = filters.inputPrice.max;
    $[163] = filters.inputPrice.min;
    $[164] = filters.outputPrice.max;
    $[165] = filters.outputPrice.min;
    $[166] = filters.selectedProvider;
    $[167] = flattenedRows;
    $[168] = hasActiveFilters;
    $[169] = modelsWithProviders;
    $[170] = pathname;
    $[171] = providers;
    $[172] = router;
    $[173] = safePage;
    $[174] = searchParams;
    $[175] = searchQuery;
    $[176] = showFilters;
    $[177] = sortDirection;
    $[178] = sortField;
    $[179] = t;
    $[180] = title;
    $[181] = totalModelCount;
    $[182] = totalPages;
    $[183] = totalProviderCount;
    $[184] = updateUrlWithFilters;
    $[185] = viewMode;
    $[186] = T0;
    $[187] = goToPage;
    $[188] = t60;
    $[189] = t61;
    $[190] = t62;
    $[191] = t63;
    $[192] = t64;
    $[193] = t65;
    $[194] = t66;
    $[195] = t67;
    $[196] = t68;
    $[197] = t69;
    $[198] = t70;
    $[199] = t71;
  } else {
    T0 = $[186];
    goToPage = $[187];
    t60 = $[188];
    t61 = $[189];
    t62 = $[190];
    t63 = $[191];
    t64 = $[192];
    t65 = $[193];
    t66 = $[194];
    t67 = $[195];
    t68 = $[196];
    t69 = $[197];
    t70 = $[198];
    t71 = $[199];
  }
  let t72;
  if ($[402] !== goToPage || $[403] !== safePage || $[404] !== t || $[405] !== totalPages) {
    t72 = totalPages > 1 && <nav aria-label="Pagination" className="flex items-center justify-center gap-2 pt-4"><Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => goToPage(safePage - 1)}><ChevronLeft className="h-4 w-4 mr-1" />{t("public.common.previous")}</Button><span className="text-sm text-muted-foreground px-2">{t("public.common.page")} {safePage} {t("public.common.of")}{" "}{totalPages}</span><Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => goToPage(safePage + 1)}>{t("public.common.next")}<ChevronRight className="h-4 w-4 ml-1" /></Button></nav>;
    $[402] = goToPage;
    $[403] = safePage;
    $[404] = t;
    $[405] = totalPages;
    $[406] = t72;
  } else {
    t72 = $[406];
  }
  let t73;
  if ($[407] !== t60 || $[408] !== t61 || $[409] !== t62 || $[410] !== t63 || $[411] !== t64 || $[412] !== t65 || $[413] !== t72) {
    t73 = <div className={t60}>{t61}{t62}{t63}{t64}{t65}{t72}</div>;
    $[407] = t60;
    $[408] = t61;
    $[409] = t62;
    $[410] = t63;
    $[411] = t64;
    $[412] = t65;
    $[413] = t72;
    $[414] = t73;
  } else {
    t73 = $[414];
  }
  let t74;
  if ($[415] !== T0 || $[416] !== t66 || $[417] !== t67 || $[418] !== t73) {
    t74 = <T0 delayDuration={t66} skipDelayDuration={t67}>{t73}</T0>;
    $[415] = T0;
    $[416] = t66;
    $[417] = t67;
    $[418] = t73;
    $[419] = t74;
  } else {
    t74 = $[419];
  }
  let t75;
  if ($[420] !== t68 || $[421] !== t74) {
    t75 = <div className={t68}>{t74}</div>;
    $[420] = t68;
    $[421] = t74;
    $[422] = t75;
  } else {
    t75 = $[422];
  }
  let t76;
  if ($[423] !== t69 || $[424] !== t75) {
    t76 = <main>{t69}{t75}</main>;
    $[423] = t69;
    $[424] = t75;
    $[425] = t76;
  } else {
    t76 = $[425];
  }
  let t77;
  if ($[426] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <Footer />;
    $[426] = t77;
  } else {
    t77 = $[426];
  }
  let t78;
  if ($[427] !== t70 || $[428] !== t71 || $[429] !== t76) {
    t78 = <div className={t70}>{t71}{t76}{t77}</div>;
    $[427] = t70;
    $[428] = t71;
    $[429] = t76;
    $[430] = t78;
  } else {
    t78 = $[430];
  }
  return t78;
}
function _temp49(m_1) {
  return m_1.free;
}
function _temp48(m_0) {
  return m_0.providerDetails.some(_temp47);
}
function _temp47(p_40) {
  return p_40.provider.tools;
}
function _temp46(m) {
  return m.providerDetails.some(_temp45);
}
function _temp45(p_39) {
  return p_39.provider.vision;
}
function _temp44(a_1, b_1) {
  return a_1 + b_1;
}
function _temp43(provider_1) {
  const ProviderIcon = getProviderIcon(provider_1.id);
  return <SelectItem key={`${provider_1.id}-${provider_1.name}`} value={provider_1.id}><div className="flex items-center gap-2">{ProviderIcon && <ProviderIcon className="h-4 w-4" />}<span>{provider_1.name}</span></div></SelectItem>;
}
function _temp42(stability) {
  return stability !== null && stability !== undefined && ["unstable", "experimental"].includes(stability);
}
function _temp41(model_4) {
  return model_4.providerDetails.map(_temp40);
}
function _temp40(p_38) {
  return p_38.provider.providerId;
}
function _temp39(p_25) {
  return parseFloat(p_25);
}
function _temp38(p_24) {
  return p_24 !== null && p_24 !== undefined;
}
function _temp37(p_23) {
  return p_23.provider.cachedInputPrice;
}
function _temp36(p_22) {
  return parseFloat(p_22);
}
function _temp35(p_21) {
  return p_21 !== null && p_21 !== undefined;
}
function _temp34(p_20) {
  return p_20.provider.cachedInputPrice;
}
function _temp33(p_31) {
  return parseFloat(p_31);
}
function _temp32(p_30) {
  return p_30 !== null && p_30 !== undefined;
}
function _temp31(p_29) {
  return p_29.provider.outputPrice;
}
function _temp30(p_28) {
  return parseFloat(p_28);
}
function _temp29(p_27) {
  return p_27 !== null && p_27 !== undefined;
}
function _temp28(p_26) {
  return p_26.provider.outputPrice;
}
function _temp27(p_37) {
  return parseFloat(p_37);
}
function _temp26(p_36) {
  return p_36 !== null && p_36 !== undefined;
}
function _temp25(p_35) {
  return p_35.provider.inputPrice;
}
function _temp24(p_34) {
  return parseFloat(p_34);
}
function _temp23(p_33) {
  return p_33 !== null && p_33 !== undefined;
}
function _temp22(p_32) {
  return p_32.provider.inputPrice;
}
function _temp21(p_15) {
  return p_15.provider.discount;
}
function _temp20(p_14) {
  return p_14.provider.requestPrice && parseFloat(p_14.provider.requestPrice) > 0;
}
function _temp19(p_13) {
  return p_13.provider.webSearch;
}
function _temp18(p_12) {
  return p_12.provider.jsonOutputSchema;
}
function _temp17(p_11) {
  return p_11.provider.jsonOutput;
}
function _temp16(p_10) {
  return p_10.provider.reasoningMaxTokens;
}
function _temp15(p_9) {
  return p_9.provider.reasoning;
}
function _temp14(p_8) {
  return p_8.provider.tools;
}
function _temp13(p_7) {
  return p_7.provider.vision;
}
function _temp12(p_6) {
  return p_6.provider.streaming;
}
function _temp11(p_1) {
  return p_1.provider.vision;
}
function _temp10(p_2) {
  return p_2.provider.streaming;
}
function _temp9(p_3) {
  return p_3.provider.reasoning;
}
function _temp8(p_4) {
  return p_4.provider.streaming && p_4.provider.cachedInputPrice !== null;
}
function _temp7(p_5) {
  return (p_5.provider.jsonOutput ?? p_5.provider.jsonOutputSchema) && p_5.provider.tools && p_5.provider.streaming && p_5.provider.cachedInputPrice !== null;
}
function _temp6(p_0) {
  return [p_0.provider.providerId, p_0.providerInfo?.name ?? ""];
}
function _temp5(t_0) {
  return t_0.replace(/[^a-z0-9]/g, "");
}
function _temp4(str) {
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
}
function _temp3(model_1) {
  return model_1.providerDetails.length > 0;
}
function _temp(e_0) {
  return e_0.stopPropagation();
}
function _temp2(t0) {
  const {
    icon: Icon,
    label,
    color
  } = t0;
  return <div key={label} className="p-0.5" title={label}><Icon className={`h-4 w-4 ${color}`} /></div>;
}